import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import UrgencyBar from "./components/UrgencyBar";
import HeroSection from "./components/HeroSection";
import EnemySection from "./components/EnemySection";
import ProcessSection from "./components/ProcessSection";
import MechanismSection from "./components/MechanismSection";
import CustomerResultsSection from "./components/CustomerResultsSection";
import ValueStackSection from "./components/ValueStackSection";
import FinalPivotSection from "./components/FinalPivotSection";
import FooterSection from "./components/FooterSection";
import MobileStickyCta from "./components/MobileStickyCta";
import { TimerProvider } from "./context/TimerContext";
import { productImages } from "./lib/productImages";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import SmsTermsPage from "./pages/SmsTermsPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import ContactPage from "./pages/ContactPage";
import ThankYouPage from "./pages/ThankYouPage";
import ScrollToTop from "./components/ScrollToTop";

const Home = () => {
  useEffect(() => {
    const heroImagePreload = document.createElement("link");
    heroImagePreload.rel = "preload";
    heroImagePreload.as = "image";
    heroImagePreload.href = productImages.main.preload;
    heroImagePreload.type = "image/webp";
    heroImagePreload.setAttribute("imagesrcset", productImages.main.webpSrcSet);
    heroImagePreload.setAttribute("imagesizes", "(min-width: 1024px) 36vw, (min-width: 640px) 70vw, 92vw");
    document.head.appendChild(heroImagePreload);

    const heroLeftPreload = document.createElement("link");
    heroLeftPreload.rel = "preload";
    heroLeftPreload.as = "image";
    heroLeftPreload.href = productImages.heroLeft.preload;
    heroLeftPreload.type = "image/webp";
    heroLeftPreload.setAttribute("imagesrcset", productImages.heroLeft.webpSrcSet);
    heroLeftPreload.setAttribute("imagesizes", "(min-width: 1024px) 62vw, 92vw");
    document.head.appendChild(heroLeftPreload);

    // Inject SEO Structured Data (JSON-LD)
    const structuredData = [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "Debt Collector Kill Switch",
        "description": "A digital self-help document system containing debt-validation demands, credit-bureau collection disputes, CFPB complaint templates, response escalations, lawsuit resources, garnishment resources, worksheets and tracking tools.",
        "brand": {
          "@type": "Brand",
          "name": "Debt Collector Kill Switch"
        },
        "sku": "DCKS-34",
        "category": "Digital consumer-rights document system",
        "image": "https://debtcollectorkillswitch.com/images/products/debt-collector-kill-switch-main.jpg",
        "offers": {
          "@type": "Offer",
          "priceCurrency": "USD",
          "price": "47.00",
          "availability": "https://schema.org/InStock",
          "url": "https://tdcrconsulting.com/debt-collector-kill-switch/checkout",
          "itemCondition": "https://schema.org/NewCondition",
          "seller": {
            "@type": "Organization",
            "name": "TDCR Consulting LLC"
          }
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "TDCR Consulting LLC",
        "url": "https://debtcollectorkillswitch.com/",
        "brand": {
          "@type": "Brand",
          "name": "Debt Collector Kill Switch"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Debt Collector Kill Switch",
        "url": "https://debtcollectorkillswitch.com/",
        "description": "A complete digital debt-collection response and collection-dispute document system."
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "HOW QUICKLY CAN I START?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Immediately. Because this is a digital self-help system, your direct dashboard link is issued instantly post-payment. You can open the numbered situation guide, identify your collector, fill in the custom validation fields, and have your first package printed and ready within approximately 30 to 60 minutes."
            }
          },
          {
            "@type": "Question",
            "name": "HOW LONG CAN THE COMPLETE PROCESS TAKE?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It depends heavily on the collector and the scenario. Some collectors request deletion in as little as 7 to 14 days of receiving physical certified packages, while others attempt automated standard responses that require you to deploy a Round 2 rebuttal (Rebuttal File #14) or escalate a CFPB case. The systems lay out every timeline scenario."
            }
          },
          {
            "@type": "Question",
            "name": "WHAT IF THEY SEND ME A STACK OF STATEMENTS?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A computerized billing printout or basic invoice statement is NOT full legal account validation under federal laws. You will open the Weak Validation Rebuttal System, check off the specific verification areas they ignored, demand they resolve the discrepancies, and escalate the record to federal consumer bureaus."
            }
          },
          {
            "@type": "Question",
            "name": "WHAT IF THEY IGNORE ME?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "This is a very common scenario. Many debt buyers hope you simply forget the account. If they fail to verify or provide proper account validation within the statutory FDCPA period, yet continue reporting or requesting payments, you open our No-Response Escalation templates and CFBP complaint guide to enforce strict compliance."
            }
          },
          {
            "@type": "Question",
            "name": "WHAT IF I HAVE ALREADY BEEN SUED OR GARNISHED?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You must act extremely quickly. Missing response deadlines lets them obtain automatic default judgments, resulting in wage levies. You will immediately open our Lawsuit & Garnishment Emergency System. Follow our step-by-step checklists to prepare documents for court filings, and assess options including licensed local attorney reviews."
            }
          },
          {
            "@type": "Question",
            "name": "HOW DO I RECEIVE EVERYTHING?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You receive immediate private digital dashboard access. There is nothing to wait for in the mail. All 34 editable templates, binders, trackers, and step-by-step PDF scenario guidebooks are available for immediate download in standard compatible formats on any phone, tablet, or desktop computer."
            }
          }
        ]
      }
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(heroImagePreload);
      document.head.removeChild(heroLeftPreload);
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-brand-dark overflow-x-hidden selection:bg-brand-yellow/30 selection:text-brand-dark">
      {/* 1. Slim Sticky Urgency Bar & Countdown Timer */}
      <UrgencyBar />

      {/* Main Container max width limit */}
      <main className="w-full">
        {/* 2. Section 1: Hero Block */}
        <HeroSection />

        {/* 3. Section 1.5: Problem / Enemy Section */}
        <EnemySection />

        {/* 3.5 Section 1.75: Process / How To Fix It */}
        <ProcessSection />

        {/* 5. Section 4: Unique Mechanism (68 demands) & Process */}
        <MechanismSection />

        {/* 7. Section 6: Redacted Dispute Correspondence Results */}
        <CustomerResultsSection />

        {/* 8. Section 7: Deep Value Stack Composition */}
        <ValueStackSection />

        {/* 9. Section 8: Final Pivot Copy & Bottom CTA Block */}
        <FinalPivotSection />
      </main>

      {/* 11. Minimal Footer & Regulatory Self-Help Disclaimer */}
      <FooterSection />

      {/* 12. Mobile Bottom-Sticky Action Bar (Hides in Footer) */}
      <MobileStickyCta />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <TimerProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/sms-terms" element={<SmsTermsPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
          </Routes>
        </TimerProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
